import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  }, [location]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      setMobileCatOpen(false);
    }
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg font-heading font-bold whitespace-nowrap ${
              isScrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}
          >
            Superbuy Spreadsheet
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap transition-colors ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
              }`}
            >
              Home
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1 text-sm font-semibold whitespace-nowrap transition-colors ${
                  isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base text-foreground-400`}></i>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/what-is-superbuy-spreadsheet"
              className={`text-sm font-semibold whitespace-nowrap transition-colors ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
              }`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/#guides"
              className={`text-sm font-semibold whitespace-nowrap transition-colors ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
              }`}
            >
              Blog
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${
              isScrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-3 pb-5 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              <i className="ri-home-4-line text-foreground-500"></i>
              Home
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-foreground-500"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-foreground-400 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-8 border-l-2 border-background-200/70 pl-3 my-1 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm text-foreground-400`}></i>
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/what-is-superbuy-spreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              <i className="ri-book-open-line text-foreground-500"></i>
              Shopping Guide
            </Link>

            <Link
              to="/#guides"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg whitespace-nowrap"
            >
              <i className="ri-article-line text-foreground-500"></i>
              Blog
            </Link>

            <div className="px-4 pt-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}